import { useEffect, useState } from "react";
import {
  Users,
  Shield,
  KeyRound,
  Settings2,
  Palette,
  Activity,
  Plus,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Copy,
  Lock,
  Trash2,
  BookOpen,
} from "lucide-react";
import type { Session } from "../../shared/contracts.js";
import { api } from "../api.js";
import "./panels.css";
import AppearancePolicyEditor from "../components/AppearancePolicyEditor";
import HelpPackageAdmin from "./HelpPackageAdmin";
type Tab =
  | "users"
  | "roles"
  | "api"
  | "services"
  | "branding"
  | "audit"
  | "organizations"
  | "appearance"
  | "help";
const roleChoices = [
  ["tenant-super-admin", "Tenant Super Admin"],
  ["studio-admin", "Studio Admin"],
  ["repository-admin", "Repository Admin"],
  ["modeler", "Data modeler"],
  ["developer", "Developer"],
  ["viewer", "Model viewer"],
];
const roleText: Record<string, string> = {
  "tenant-super-admin": "Tenant roles, security and delegated administration.",
  "studio-admin": "Studio preferences and approved integration settings.",
  "repository-admin": "Administration within assigned repositories.",
  modeler: "Create and change model structures under repository grants.",
  developer: "Add ordinary physical columns to existing tables by default.",
  viewer: "Read authorized models and use personal view preferences.",
};
export default function AdminPanel({
  session,
  onChanged,
}: {
  session: Session;
  onChanged?: () => void;
}) {
  const [tab, setTab] = useState<Tab>(
    session.permissions.includes("users.manage")
      ? "users"
      : session.permissions.includes("platform.manage")
        ? "organizations"
        : "branding",
  );
  const [tenants, setTenants] = useState<any[]>([]);
  const [tenant, setTenant] = useState({
    organizationCode: "",
    organizationName: "",
    name: "",
    email: "",
    password: "",
  });
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [repos, setRepos] = useState<any[]>([]);
  const [audit, setAudit] = useState<any[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [settings, setSettings] = useState<any>({ branding: session.branding });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [newUser, setNewUser] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    roles: ["viewer"],
    repositoryIds: [] as string[],
  });
  const [newRole, setNewRole] = useState(false);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [role, setRole] = useState({
    name: "",
    description: "",
    permissions: [] as string[],
  });
  const [service, setService] = useState({
    name: "",
    permissions: ["model.read"],
    repositoryIds: [] as string[],
  });
  const [token, setToken] = useState("");
  const [explain, setExplain] = useState(false);
  const canStudio = session.permissions.includes("studio.configure");
  const canRoles = session.permissions.includes("roles.manage");
  const canUsers = session.permissions.includes("users.manage");
  const allRoleChoices = [
    ...roleChoices,
    ...roles
      .filter((r) => !r.builtin && !roleChoices.some(([id]) => id === r.id))
      .map((r) => [r.id, r.name]),
  ];
  async function load() {
    setBusy(true);
    setError("");
    if (tab === "appearance" || tab === "help") { setBusy(false); return; }
    const routes: Record<Exclude<Tab, "appearance" | "help">, string> = {
      users: "/administration/users",
      roles: "/administration/roles",
      api: "/administration/api-configuration",
      services: "/service-principals",
      branding: "/administration/settings",
      audit: "/administration/audit",
      organizations: "/administration/tenants",
    };
    try {
      const value = await api(routes[tab]);
      if (tab === "organizations")
        setTenants(Array.isArray(value) ? value : []);
      if (tab === "users")
        setUsers(Array.isArray(value) ? value : value.users || []);
      if (tab === "roles")
        setRoles(Array.isArray(value) ? value : value.roles || []);
      if (tab === "api") setConfig(value);
      if (tab === "services")
        setServices(Array.isArray(value) ? value : value.items || []);
      if (tab === "branding") setSettings(value);
      if (tab === "audit")
        setAudit(Array.isArray(value) ? value : value.items || []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void load();
  }, [tab]);
  useEffect(() => {
    api("/repositories")
      .then((r) => setRepos(Array.isArray(r) ? r : []))
      .catch(() => {});
    api("/administration/api-configuration")
      .then(setConfig)
      .catch(() => {});
    api("/administration/roles")
      .then((r) => setRoles(Array.isArray(r) ? r : []))
      .catch(() => {});
  }, []);
  async function act(fn: () => Promise<any>, message: string) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
      setNotice(message);
      await load();
      onChanged?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const permissions = [
    ...new Set([
      ...(config?.allowedPermissions || []),
      ...roles.flatMap((r) => r.permissions || []),
    ]),
  ].sort() as string[];
  const tabs: [Tab, string, typeof Users][] = [
    ["users", "People", Users],
    ["roles", "Roles", Shield],
    ["api", "API policy", Settings2],
    ["services", "Service clients", KeyRound],
    ["branding", "Studio", Palette],
    ["appearance", "Appearance", Palette],
    ["audit", "Audit", Activity],
  ];
  if (canStudio) tabs.push(["help", "Help packages", BookOpen]);
  if (session.permissions.includes("platform.manage"))
    tabs.push(["organizations", "Organizations", Shield]);
  return (
    <div className="dgx-admin" data-testid="admin-panel">
      <div className="dgx-admin-summary">
        <div>
          <span className="dgx-eyebrow">ORGANIZATION ADMINISTRATION</span>
          <h2>{session.organizationCode}</h2>
        </div>
        <span className="dgx-scope">
          <Shield size={14} />
          Scoped to your current grants
        </span>
      </div>
      <nav className="dgx-admin-tabs" aria-label="Administration sections">
        {tabs.map(([id, label, Icon]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => {
              setTab(id);
              setNotice("");
            }}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
        <button
          title="Refresh administration"
          aria-label="Refresh administration"
          onClick={() => void load()}
        >
          <RefreshCw size={15} className={busy ? "dgx-spin" : ""} />
        </button>
      </nav>
      {error && (
        <div className="dgx-error" role="alert">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      {notice && (
        <div className="dgx-success" role="status">
          <CheckCircle2 size={16} />
          {notice}
        </div>
      )}
      {tab === "appearance" && <AppearancePolicyEditor canEdit={canStudio} onChanged={onChanged} />}
      {tab === "help" && <HelpPackageAdmin session={session} />}
      {tab === "users" && (
        <>
          <div className="dgx-section-title">
            <div>
              <h3>People and access</h3>
              <p>Assign working roles and explicit repository scope.</p>
            </div>
            <button
              className="dgx-primary"
              onClick={() => {
                setEditingUser(null);
                setUser({
                  name: "",
                  email: "",
                  password: "",
                  roles: ["viewer"],
                  repositoryIds: [],
                });
                setNewUser(!newUser);
              }}
              disabled={!canUsers}
              title={
                !canUsers
                  ? "User management permission is required."
                  : undefined
              }
            >
              <Plus size={15} />
              Add person
            </button>
          </div>
          {newUser && (
            <form
              className="dgx-form dgx-callout dgx-form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                void act(
                  async () => {
                    await api(
                      editingUser
                        ? `/administration/users/${editingUser}`
                        : "/administration/users",
                      {
                        method: editingUser ? "PATCH" : "POST",
                        body: JSON.stringify({
                          ...user,
                          ...(!user.password ? { password: undefined } : {}),
                          repositoryIds: user.repositoryIds.length
                            ? user.repositoryIds
                            : null,
                        }),
                      },
                    );
                    setNewUser(false);
                    setUser({
                      name: "",
                      email: "",
                      password: "",
                      roles: ["viewer"],
                      repositoryIds: [],
                    });
                  },
                  editingUser ? "Membership updated." : "Person created.",
                );
              }}
            >
              <label>
                Name
                <input
                  required
                  value={user.name}
                  onChange={(e) => setUser({ ...user, name: e.target.value })}
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  required
                  disabled={!!editingUser}
                  value={user.email}
                  onChange={(e) => setUser({ ...user, email: e.target.value })}
                />
              </label>
              <label>
                Initial password
                <input
                  required={!editingUser}
                  minLength={12}
                  autoComplete="new-password"
                  type="password"
                  value={user.password}
                  onChange={(e) =>
                    setUser({ ...user, password: e.target.value })
                  }
                />
                <small>
                  At least 12 characters. Share through your approved private
                  channel.
                </small>
              </label>
              <fieldset>
                <legend>Role assignments</legend>
                <div className="dgx-checks">
                  {allRoleChoices.map(([id, name]) => (
                    <label key={id}>
                      <input
                        type="checkbox"
                        checked={user.roles.includes(id)}
                        onChange={(e) =>
                          setUser({
                            ...user,
                            roles: e.target.checked
                              ? [...user.roles, id]
                              : user.roles.filter((r) => r !== id),
                          })
                        }
                      />
                      {name}
                    </label>
                  ))}
                </div>
              </fieldset>
              <fieldset className="dgx-span">
                <legend>Repository scope</legend>
                <p className="dgx-muted">
                  No selection means all repositories permitted by the role.
                  Select repositories to restrict membership.
                </p>
                <div className="dgx-checks">
                  {repos.map((r) => (
                    <label key={r.repositoryId}>
                      <input
                        type="checkbox"
                        checked={user.repositoryIds.includes(r.repositoryId)}
                        onChange={(e) =>
                          setUser({
                            ...user,
                            repositoryIds: e.target.checked
                              ? [...user.repositoryIds, r.repositoryId]
                              : user.repositoryIds.filter(
                                  (x) => x !== r.repositoryId,
                                ),
                          })
                        }
                      />
                      {r.name}
                    </label>
                  ))}
                </div>
              </fieldset>
              <button
                className="dgx-primary"
                disabled={busy || !user.roles.length}
              >
                {editingUser ? "Update membership" : "Create person"}
              </button>
              <button type="button" onClick={() => setNewUser(false)}>
                Cancel
              </button>
            </form>
          )}
          <div className="dgx-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Roles</th>
                  <th>Scope</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <strong>{u.name}</strong>
                      <small>{u.email}</small>
                    </td>
                    <td>
                      {(u.roles || []).map((r: string) => (
                        <span className="dgx-tag" title={roleText[r]} key={r}>
                          {roleChoices.find((x) => x[0] === r)?.[1] || r}
                        </span>
                      ))}
                    </td>
                    <td>
                      {u.repositoryIds === null
                        ? "Role scope"
                        : `${u.repositoryIds?.length || 0} repositories`}
                    </td>
                    <td>
                      <span
                        className={u.active ? "dgx-text-success" : "dgx-muted"}
                      >
                        {u.active ? "Active" : "Suspended"}
                      </span>
                    </td>
                    <td>
                      {canUsers && u.id !== session.userId && (
                        <>
                          <button
                            className="dgx-link"
                            onClick={() => {
                              setEditingUser(u.id);
                              setUser({
                                name: u.name,
                                email: u.email,
                                password: "",
                                roles: u.roles,
                                repositoryIds: u.repositoryIds || [],
                              });
                              setNewUser(true);
                            }}
                          >
                            Edit access
                          </button>
                          <button
                            className="dgx-link"
                            onClick={() =>
                              void act(
                                () =>
                                  api(`/administration/users/${u.id}`, {
                                    method: "PATCH",
                                    body: JSON.stringify({ active: !u.active }),
                                  }),
                                u.active
                                  ? "Membership suspended."
                                  : "Membership activated.",
                              )
                            }
                          >
                            {u.active ? "Suspend" : "Activate"}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!users.length && !busy && (
              <p className="dgx-empty">
                No memberships are visible under your grants.
              </p>
            )}
          </div>
        </>
      )}
      {tab === "roles" && (
        <>
          <div className="dgx-section-title">
            <div>
              <h3>Role definitions</h3>
              <p>
                Custom roles combine explicit permissions. Administrator names
                are application scopes.
              </p>
            </div>
            <button
              className="dgx-primary"
              disabled={!canRoles}
              title={!canRoles ? "Tenant Super Admin is required." : undefined}
              onClick={() => {
                setEditingRole(null);
                setRole({ name: "", description: "", permissions: [] });
                setNewRole(!newRole);
              }}
            >
              <Plus size={15} />
              Create role
            </button>
          </div>
          <div className="dgx-role-grid">
            {roles.map((r) => (
              <article key={r.id}>
                <Shield size={19} />
                <h4>{r.name}</h4>
                <p>{r.description || roleText[r.id]}</p>
                <span className="dgx-eyebrow">
                  {r.builtin ? "Built-in template" : "Custom role"}
                </span>
                <details>
                  <summary>{r.permissions?.length || 0} permissions</summary>
                  <ul>
                    {r.permissions?.map((p: string) => (
                      <li key={p}>
                        <code>{p}</code>
                      </li>
                    ))}
                  </ul>
                </details>
                {canRoles &&
                  !["tenant-super-admin", "platform-super-admin"].includes(
                    r.id,
                  ) && (
                    <button
                      className="dgx-link"
                      onClick={() => {
                        setEditingRole(r.id);
                        setRole({
                          name: r.name,
                          description: r.description || "",
                          permissions: r.permissions || [],
                        });
                        setNewRole(true);
                      }}
                    >
                      Edit privileges
                    </button>
                  )}
              </article>
            ))}
          </div>
          {newRole && (
            <form
              className="dgx-form dgx-callout"
              onSubmit={(e) => {
                e.preventDefault();
                void act(
                  async () => {
                    await api(
                      editingRole
                        ? `/administration/roles/${editingRole}`
                        : "/administration/roles",
                      {
                        method: editingRole ? "PATCH" : "POST",
                        body: JSON.stringify(role),
                      },
                    );
                    setNewRole(false);
                    setRole({ name: "", description: "", permissions: [] });
                  },
                  editingRole
                    ? "Role privileges updated."
                    : "Custom role created.",
                );
              }}
            >
              <label>
                Role name
                <input
                  required
                  value={role.name}
                  onChange={(e) => setRole({ ...role, name: e.target.value })}
                />
              </label>
              <label>
                Purpose
                <textarea
                  required
                  value={role.description}
                  onChange={(e) =>
                    setRole({ ...role, description: e.target.value })
                  }
                />
              </label>
              <fieldset>
                <legend>Explicit grants</legend>
                <div className="dgx-checks">
                  {permissions.map((p) => (
                    <label key={p}>
                      <input
                        type="checkbox"
                        checked={role.permissions.includes(p)}
                        onChange={(e) =>
                          setRole({
                            ...role,
                            permissions: e.target.checked
                              ? [...role.permissions, p]
                              : role.permissions.filter((x) => x !== p),
                          })
                        }
                      />
                      <code>{p}</code>
                    </label>
                  ))}
                </div>
              </fieldset>
              <button
                className="dgx-primary"
                disabled={busy || !role.permissions.length}
              >
                {editingRole ? "Update role" : "Create role"}
              </button>
              <button type="button" onClick={() => setNewRole(false)}>
                Cancel
              </button>
            </form>
          )}
        </>
      )}
      {tab === "api" && config && (
        <>
          <div className="dgx-section-title">
            <div>
              <h3>API policy</h3>
              <p>
                Effective tenant configuration, bounded by installation policy
                and delegated rights.
              </p>
            </div>
            <button className="dgx-link" onClick={() => setExplain(!explain)}>
              <BookOpen size={15} />
              Configuration help
            </button>
          </div>
          {explain && (
            <div className="dgx-callout">
              <p>
                Tenant Super Admin enables external access and service
                credentials. Studio Admin can change delegated operational
                settings only. The server validates every setting and checks the
                configuration version before activation.
              </p>
              <p>
                External access exposes supported API routes to registered
                clients; it does not grant model permissions. Each service
                principal still needs its own repository and operation scopes.
              </p>
            </div>
          )}
          <form
            className="dgx-form"
            onSubmit={(e) => {
              e.preventDefault();
              void act(
                () =>
                  api("/administration/api-configuration", {
                    method: "PATCH",
                    headers: { "If-Match": String(config.version ?? 0) },
                    body: JSON.stringify(
                      Object.fromEntries(
                        (config.editablePaths || ["maxIngestionBytes"]).map(
                          (key: string) => [key, config[key]],
                        ),
                      ),
                    ),
                  }),
                "API policy updated.",
              );
            }}
          >
            <div className="dgx-setting-row">
              <div>
                <strong>External API access</strong>
                <p>
                  Permit registered service clients under explicit tenant
                  policy.
                </p>
              </div>
              <input
                aria-label="External API access"
                type="checkbox"
                checked={!!config.enabled}
                disabled={!config.editablePaths?.includes("enabled")}
                onChange={(e) =>
                  setConfig({ ...config, enabled: e.target.checked })
                }
              />
            </div>
            <div className="dgx-setting-row">
              <div>
                <strong>Service credentials</strong>
                <p>
                  Allow authorized administrators to issue scoped machine
                  credentials.
                </p>
              </div>
              <input
                aria-label="Service credentials"
                type="checkbox"
                checked={!!config.serviceTokensEnabled}
                disabled={
                  !config.editablePaths?.includes("serviceTokensEnabled")
                }
                onChange={(e) =>
                  setConfig({
                    ...config,
                    serviceTokensEnabled: e.target.checked,
                  })
                }
              />
            </div>
            <label>
              Maximum ingestion size (bytes)
              <input
                type="number"
                min={1024}
                max={20971520}
                value={config.maxIngestionBytes || 1048576}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    maxIngestionBytes: Number(e.target.value),
                  })
                }
              />
              <small>
                Effective platform ceiling still applies. Oversized requests are
                rejected before model mutation.
              </small>
            </label>
            {config.editablePaths?.includes("allowedPermissions") && (
              <fieldset>
                <legend>Maximum service operation grants</legend>
                <p className="dgx-muted">
                  Service clients cannot receive permissions outside this
                  ceiling.
                </p>
                <div className="dgx-checks">
                  {[
                    "model.read",
                    "model.create",
                    "model.edit",
                    "model.publish",
                    "model.export",
                  ].map((p) => (
                    <label key={p}>
                      <input
                        type="checkbox"
                        checked={(config.allowedPermissions || []).includes(p)}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            allowedPermissions: e.target.checked
                              ? [...(config.allowedPermissions || []), p]
                              : (config.allowedPermissions || []).filter(
                                  (x: string) => x !== p,
                                ),
                          })
                        }
                      />
                      <code>{p}</code>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            {config.editablePaths?.includes("allowedRepositoryIds") && (
              <fieldset>
                <legend>Service repository ceiling</legend>
                <label>
                  <input
                    type="checkbox"
                    checked={config.allowedRepositoryIds === null}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        allowedRepositoryIds: e.target.checked ? null : [],
                      })
                    }
                  />
                  Permit all repositories within each service's explicit scope
                </label>
                {config.allowedRepositoryIds !== null && (
                  <div className="dgx-checks">
                    {repos.map((r) => (
                      <label key={r.repositoryId}>
                        <input
                          type="checkbox"
                          checked={(config.allowedRepositoryIds || []).includes(
                            r.repositoryId,
                          )}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              allowedRepositoryIds: e.target.checked
                                ? [
                                    ...(config.allowedRepositoryIds || []),
                                    r.repositoryId,
                                  ]
                                : (config.allowedRepositoryIds || []).filter(
                                    (id: string) => id !== r.repositoryId,
                                  ),
                            })
                          }
                        />
                        {r.name}
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>
            )}
            <div className="dgx-actions">
              <button
                type="button"
                onClick={() =>
                  void act(async () => {
                    const r = await api(
                      "/administration/api-configuration/validate",
                      {
                        method: "POST",
                        body: JSON.stringify(
                          Object.fromEntries(
                            (config.editablePaths || ["maxIngestionBytes"]).map(
                              (key: string) => [key, config[key]],
                            ),
                          ),
                        ),
                      },
                    );
                    if (!r.valid)
                      throw new Error(
                        (r.diagnostics || [])
                          .map((d: any) => d.message)
                          .join("; ") || "Configuration is invalid.",
                      );
                  }, "Configuration validated. No setting was activated.")
                }
              >
                Validate draft
              </button>
              <button className="dgx-primary" disabled={busy}>
                Activate configuration
              </button>
            </div>
            <p className="dgx-footnote">
              <Lock size={13} />
              Configuration version {config.version ?? 0}. Authentication and
              permission checks cannot be disabled here.
            </p>
          </form>
        </>
      )}
      {tab === "services" && (
        <>
          <div className="dgx-section-title">
            <div>
              <h3>Service clients</h3>
              <p>
                One identity per integration, with only the permissions it
                needs.
              </p>
            </div>
          </div>
          {token && (
            <div className="dgx-callout">
              <strong>Copy this credential now</strong>
              <p>
                It is shown once. Store it in the consumer's secret
                configuration.
              </p>
              <div className="dgx-secret">
                <input
                  aria-label="New service credential"
                  readOnly
                  type="password"
                  value={token}
                />
                <button
                  onClick={() => void navigator.clipboard.writeText(token)}
                  title="Copy credential"
                >
                  <Copy size={16} />
                </button>
                <button onClick={() => setToken("")}>Dismiss</button>
              </div>
            </div>
          )}
          <form
            className="dgx-form dgx-callout"
            onSubmit={(e) => {
              e.preventDefault();
              void act(async () => {
                const r = await api("/service-principals", {
                  method: "POST",
                  body: JSON.stringify(service),
                });
                setToken(r.token || r.credential || "");
                setService({ ...service, name: "" });
              }, "Service client registered.");
            }}
          >
            <label>
              Integration name
              <input
                required
                placeholder="Requirements Companion"
                value={service.name}
                onChange={(e) =>
                  setService({ ...service, name: e.target.value })
                }
              />
            </label>
            <fieldset>
              <legend>Repository scope</legend>
              <div className="dgx-checks">
                {repos.map((r) => (
                  <label key={r.repositoryId}>
                    <input
                      type="checkbox"
                      checked={service.repositoryIds.includes(r.repositoryId)}
                      onChange={(e) =>
                        setService({
                          ...service,
                          repositoryIds: e.target.checked
                            ? [...service.repositoryIds, r.repositoryId]
                            : service.repositoryIds.filter(
                                (x) => x !== r.repositoryId,
                              ),
                        })
                      }
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend>Operation scopes</legend>
              <div className="dgx-checks">
                {(
                  config?.allowedPermissions || [
                    "model.read",
                    "model.create",
                    "model.edit",
                    "model.publish",
                  ]
                ).map((p: string) => (
                  <label key={p}>
                    <input
                      type="checkbox"
                      checked={service.permissions.includes(p)}
                      onChange={(e) =>
                        setService({
                          ...service,
                          permissions: e.target.checked
                            ? [...service.permissions, p]
                            : service.permissions.filter((x) => x !== p),
                        })
                      }
                    />
                    <code>{p}</code>
                  </label>
                ))}
              </div>
            </fieldset>
            <button
              className="dgx-primary"
              disabled={
                busy ||
                !service.name ||
                !service.repositoryIds.length ||
                !service.permissions.length
              }
            >
              Issue scoped credential
            </button>
            <p className="dgx-muted">
              Select at least one repository and one permission. The server also
              requires API policy to permit issuance.
            </p>
          </form>
          <div className="dgx-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Scopes</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id || s.principalId}>
                    <td>{s.name}</td>
                    <td>{s.permissions?.join(", ")}</td>
                    <td>
                      {s.active === false || s.revoked ? "Revoked" : "Active"}
                    </td>
                    <td>
                      <button
                        title="Revoke service credential"
                        disabled={s.active === false || s.revoked}
                        onClick={() =>
                          void act(
                            () =>
                              api(
                                `/service-principals/${s.id || s.principalId}`,
                                { method: "DELETE" },
                              ),
                            "Service credential revoked.",
                          )
                        }
                      >
                        <Trash2 size={14} /> Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {tab === "branding" && (
        <>
          <div className="dgx-section-title">
            <div>
              <h3>Studio identity</h3>
              <p>
                Change the product display name and accent from one central
                setting.
              </p>
            </div>
          </div>
          <form
            className="dgx-form"
            onSubmit={(e) => {
              e.preventDefault();
              void act(
                () =>
                  api("/administration/settings", {
                    method: "PATCH",
                    body: JSON.stringify({ branding: settings.branding }),
                  }),
                "Studio identity updated.",
              );
            }}
          >
            <label>
              Studio name
              <input
                required
                maxLength={80}
                disabled={!canStudio}
                value={settings.branding?.name || ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    branding: { ...settings.branding, name: e.target.value },
                  })
                }
              />
            </label>
            <label>
              Accent color
              <div className="dgx-color">
                <input
                  aria-label="Studio accent"
                  disabled={!canStudio}
                  type="color"
                  value={settings.branding?.accent || "#256d62"}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      branding: {
                        ...settings.branding,
                        accent: e.target.value,
                      },
                    })
                  }
                />
                <code>{settings.branding?.accent}</code>
              </div>
            </label>
            <button className="dgx-primary" disabled={busy || !canStudio} title={!canStudio ? "Studio configuration permission is required." : undefined}>
              Save studio identity
            </button>
            <p className="dgx-muted">
              Canonical format identifiers remain stable for interoperability.
              Personal appearance preferences can apply where policy permits.
            </p>
          </form>
        </>
      )}
      {tab === "organizations" && (
        <>
          <div className="dgx-section-title">
            <div>
              <h3>Organizations</h3>
              <p>
                Provision an isolated tenant repository database and its first
                Tenant Super Admin.
              </p>
            </div>
          </div>
          <div className="dgx-callout">
            <p>
              Each organization uses its own database in the PostgreSQL
              deployment profile. Local development uses isolated embedded
              stores. The installation administrator receives no automatic
              modeling grant in new organizations.
            </p>
          </div>
          <form
            className="dgx-form dgx-form-grid"
            onSubmit={(e) => {
              e.preventDefault();
              void act(async () => {
                await api("/administration/tenants", {
                  method: "POST",
                  body: JSON.stringify(tenant),
                });
                setTenant({
                  organizationCode: "",
                  organizationName: "",
                  name: "",
                  email: "",
                  password: "",
                });
              }, "Organization provisioned. Its administrator can sign in using the organization code.");
            }}
          >
            <label>
              Organization code
              <input
                required
                pattern="[a-z0-9][a-z0-9-]{1,47}"
                value={tenant.organizationCode}
                onChange={(e) =>
                  setTenant({
                    ...tenant,
                    organizationCode: e.target.value.toLowerCase(),
                  })
                }
              />
              <small>Unique lowercase code used at sign-in.</small>
            </label>
            <label>
              Organization name
              <input
                required
                value={tenant.organizationName}
                onChange={(e) =>
                  setTenant({ ...tenant, organizationName: e.target.value })
                }
              />
            </label>
            <label>
              Administrator name
              <input
                required
                value={tenant.name}
                onChange={(e) => setTenant({ ...tenant, name: e.target.value })}
              />
            </label>
            <label>
              Administrator email
              <input
                required
                type="email"
                value={tenant.email}
                onChange={(e) =>
                  setTenant({ ...tenant, email: e.target.value })
                }
              />
            </label>
            <label>
              Initial administrator password
              <input
                required
                type="password"
                minLength={12}
                autoComplete="new-password"
                value={tenant.password}
                onChange={(e) =>
                  setTenant({ ...tenant, password: e.target.value })
                }
              />
            </label>
            <div className="dgx-actions">
              <button className="dgx-primary" disabled={busy}>
                Provision organization
              </button>
            </div>
          </form>
          <div className="dgx-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Sign-in code</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>
                      <code>{t.organizationCode}</code>
                    </td>
                    <td>{t.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {tab === "audit" && (
        <>
          <div className="dgx-section-title">
            <div>
              <h3>Administration activity</h3>
              <p>
                Inspect accepted actions and access decisions within your audit
                scope.
              </p>
            </div>
          </div>
          <div className="dgx-audit">
            {audit.map((a, i) => (
              <article key={a.id || i}>
                <span className="dgx-audit-dot" />
                <div>
                  <strong>{a.action || a.type || a.event}</strong>
                  <p>{a.summary || a.message || a.resourceId || ""}</p>
                  <small>
                    {a.actorName || a.actorId || a.userId || "System"} ·{" "}
                    {a.createdAt
                      ? new Date(a.createdAt).toLocaleString()
                      : a.at
                        ? new Date(a.at).toLocaleString()
                        : ""}
                  </small>
                </div>
              </article>
            ))}
            {!audit.length && !busy && (
              <p className="dgx-empty">No audit entries are visible.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
